import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Sparkles, RefreshCw, X, AlertCircle, Check } from 'lucide-react';
import api from '../../services/api';
import { useAvatar } from '../../context/AvatarContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATE_LINES = [
  'Shaping your Movement world…',
  'Lighting your Recovery world…',
  'Stocking your Nutrition world…',
  'Calming your Mental world…',
];

// An <img> can't send auth headers — append the token to the backend URL.
const withAuth = (path) => {
  if (!path) return null;
  const token = localStorage.getItem('hpme_token') || '';
  const sep = path.includes('?') ? '&' : '?';
  return `${BACKEND_URL}${path}${sep}auth=${token}`;
};

/**
 * SelfieOnboarding — TWO-STEP personal avatar pipeline:
 *  1. upload selfie → generate ONE base portrait → user APPROVES the likeness
 *  2. only then generate the 16 world scenes (the expensive run).
 * Generation runs server-side in the background, so this screen RESUMES the
 * correct phase from the live avatar_status whenever it (re)mounts — leaving
 * and coming back never restarts the run.
 */
export const SelfieOnboarding = ({ onReady, onCancel }) => {
  const { refresh } = useAvatar();
  // capture | preview | generating_base | approve | generating_states | failed
  const [phase, setPhase] = useState('loading');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [baseUrl, setBaseUrl] = useState(null);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(16);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => stopPoll(), []);

  // Map a live status payload → the screen the user should see.
  // `allowComplete` is true only while POLLING — on first mount we must NOT
  // treat an already-'ready' avatar as "just finished" (that would instantly
  // close the re-generate flow the user explicitly opened).
  const applyStatus = useCallback(async (s, allowComplete = true) => {
    const st = s?.status || 'none';
    if (st === 'ready') {
      stopPoll();
      if (allowComplete) {
        await refresh();
        window.dispatchEvent(new Event('avatar:changed'));
        window.dispatchEvent(new Event('wellness:refresh'));
        onReady?.();
      } else {
        setPhase('capture');
      }
      return;
    }
    if (st === 'failed') {
      stopPoll();
      setError(s?.error || 'Generation failed. Please try another photo.');
      setPhase('failed');
      return;
    }
    if (st === 'generating_base') { setPhase('generating_base'); return; }
    if (st === 'base_ready') {
      stopPoll();
      setBaseUrl(withAuth(s?.base_url));
      setPhase('approve');
      return;
    }
    if (st === 'generating_states') {
      setProgress(s?.progress || 0);
      setTotal(s?.total || 16);
      setPhase('generating_states');
      return;
    }
    setPhase('capture');
  }, [onReady, refresh]);

  const startPoll = useCallback(() => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try { const s = await api.getAvatarStatus(); await applyStatus(s.data, true); }
      catch (e) { /* keep polling */ }
    }, 3500);
  }, [applyStatus]);

  // On mount: resume from whatever the backend is doing (do NOT auto-complete).
  useEffect(() => {
    (async () => {
      try {
        const s = await api.getAvatarStatus();
        await applyStatus(s.data, false);
        const st = s.data?.status;
        if (st === 'generating_base' || st === 'generating_states') startPoll();
      } catch (e) { setPhase('capture'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setPhase('preview');
  };

  const startBase = async () => {
    if (!file) return;
    setPhase('generating_base');
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.generateAvatar(fd);
      startPoll();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start generation. Try again.');
      setPhase('failed');
    }
  };

  const rerollBase = async () => {
    setPhase('generating_base');
    setError(null);
    try { await api.regenerateAvatarBase(); startPoll(); }
    catch (e) { setError(e.response?.data?.detail || 'Could not regenerate.'); setPhase('failed'); }
  };

  const confirmBase = async () => {
    setPhase('generating_states');
    setProgress(0);
    setError(null);
    try { const r = await api.confirmAvatarBase(); setTotal(r.data?.total || 16); startPoll(); }
    catch (e) { setError(e.response?.data?.detail || 'Could not continue.'); setPhase('failed'); }
  };

  const newPhoto = () => { stopPoll(); setFile(null); setPreviewUrl(null); setBaseUrl(null); setPhase('capture'); };

  const pct = Math.round((progress / Math.max(1, total)) * 100);
  const stateLine = STATE_LINES[Math.min(STATE_LINES.length - 1, Math.floor((progress / Math.max(1, total)) * STATE_LINES.length))];

  return (
    <div className="hpme-card relative overflow-hidden p-6 sm:p-8 flex flex-col items-center text-center" data-testid="selfie-onboarding">
      {onCancel && phase !== 'generating_base' && phase !== 'generating_states' && (
        <button onClick={onCancel} data-testid="selfie-cancel" className="absolute top-3 right-3 text-zinc-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      )}

      <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/15 border border-primary/40 mb-4">
        <Sparkles className="w-5 h-5 text-primary" />
      </span>

      {phase === 'loading' && (
        <p className="text-sm text-zinc-400 py-8">Loading your avatar…</p>
      )}

      {phase === 'capture' && (
        <>
          <h2 className="font-secondary font-bold text-2xl sm:text-3xl tracking-tight">Meet your living avatar</h2>
          <p className="text-sm text-zinc-400 mt-2 max-w-md">
            Upload a clear, front-facing selfie and Nikki turns it into a cinematic rendered
            character that lives across your four wellness worlds — changing posture, energy and
            surroundings with your real scores.
          </p>
          <input ref={inputRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid="selfie-input" />
          <button
            onClick={() => inputRef.current?.click()}
            data-testid="selfie-capture-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-black font-medium px-6 py-3 text-sm hover:opacity-90 transition-opacity"
          >
            <Camera className="w-4 h-4" /> Take / upload a selfie
          </button>
          <p className="text-[11px] text-zinc-500 mt-3">Front-facing, well-lit, neutral expression, just you, no heavy filters. We keep it private.</p>
          <button onClick={rerollBase} data-testid="selfie-use-saved" className="mt-4 text-xs text-zinc-400 underline underline-offset-2 hover:text-zinc-200">
            Or re-render from my last selfie
          </button>
        </>
      )}

      {phase === 'preview' && (
        <>
          <h2 className="font-secondary font-bold text-2xl tracking-tight">Looking good?</h2>
          <p className="text-sm text-zinc-400 mt-2">Use this photo, or pick another.</p>
          <img src={previewUrl} alt="Your selfie" className="mt-5 w-40 h-40 object-cover rounded-2xl border border-white/15" data-testid="selfie-preview" />
          <div className="mt-6 flex items-center gap-3">
            <button onClick={newPhoto} data-testid="selfie-retake" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm text-zinc-200 hover:text-white">
              <RefreshCw className="w-4 h-4" /> Retake
            </button>
            <button onClick={startBase} data-testid="selfie-generate" className="inline-flex items-center gap-1.5 rounded-full bg-primary text-black font-medium px-6 py-2.5 text-sm hover:opacity-90">
              <Sparkles className="w-4 h-4" /> Create my character
            </button>
          </div>
          <p className="text-[11px] text-zinc-500 mt-3">Step 1 of 2: we render just your character first so you can approve it before building the worlds.</p>
        </>
      )}

      {phase === 'generating_base' && (
        <>
          {previewUrl && <img src={previewUrl} alt="" className="w-24 h-24 object-cover rounded-2xl border border-white/15 mb-5 lv-breathe" />}
          <h2 className="font-secondary font-bold text-xl tracking-tight">Rendering your character…</h2>
          <p className="text-sm text-zinc-400 mt-2" data-testid="selfie-base-line">Step 1 of 2 — shaping a cinematic version of you.</p>
          <div className="w-full max-w-sm mt-5 h-2 rounded-full bg-white/10 overflow-hidden" data-testid="selfie-base-bar">
            <div className="h-full bg-primary animate-pulse" style={{ width: '60%' }} />
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">This takes about a minute. You can leave this screen — it keeps rendering in the background.</p>
        </>
      )}

      {phase === 'approve' && (
        <>
          <h2 className="font-secondary font-bold text-2xl tracking-tight">Does this look like you?</h2>
          <p className="text-sm text-zinc-400 mt-2 max-w-sm">This is your character. Approve it and Nikki builds your four wellness worlds, or re-roll it.</p>
          {baseUrl && (
            <img src={baseUrl} alt="Your character" className="mt-5 w-48 h-64 object-cover rounded-2xl border border-white/15" data-testid="selfie-base-preview" />
          )}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button onClick={confirmBase} data-testid="selfie-approve-base" className="inline-flex items-center gap-1.5 rounded-full bg-primary text-black font-medium px-6 py-2.5 text-sm hover:opacity-90">
              <Check className="w-4 h-4" /> Looks like me — build my worlds
            </button>
            <button onClick={rerollBase} data-testid="selfie-reroll-base" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm text-zinc-200 hover:text-white">
              <RefreshCw className="w-4 h-4" /> Try again
            </button>
            <button onClick={newPhoto} data-testid="selfie-newphoto-base" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm text-zinc-200 hover:text-white">
              <Camera className="w-4 h-4" /> New photo
            </button>
          </div>
          <p className="text-[11px] text-zinc-500 mt-3">Building the 16 worlds takes about 3–5 minutes.</p>
        </>
      )}

      {phase === 'generating_states' && (
        <>
          {baseUrl && <img src={baseUrl} alt="" className="w-24 h-32 object-cover rounded-2xl border border-white/15 mb-5 lv-breathe" />}
          <h2 className="font-secondary font-bold text-xl tracking-tight">Building your wellness worlds…</h2>
          <p className="text-sm text-zinc-400 mt-2" data-testid="selfie-progress-line">{stateLine}</p>
          <div className="w-full max-w-sm mt-5 h-2 rounded-full bg-white/10 overflow-hidden" data-testid="selfie-progress-bar">
            <div className="h-full bg-primary transition-[width] duration-700" style={{ width: `${Math.max(6, pct)}%` }} />
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">{progress} / {total} worlds · this takes about 3–5 minutes. You can leave this screen — Nikki keeps building in the background and it'll be ready when you return.</p>
        </>
      )}

      {phase === 'failed' && (
        <>
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-500/15 border border-red-500/40 mb-2 -mt-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
          </span>
          <h2 className="font-secondary font-bold text-xl tracking-tight">That didn't work</h2>
          <p className="text-sm text-zinc-400 mt-2 max-w-sm" data-testid="selfie-error">{error}</p>
          <button onClick={newPhoto} data-testid="selfie-retry" className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-primary text-black font-medium px-6 py-2.5 text-sm">
            <RefreshCw className="w-4 h-4" /> Try another photo
          </button>
        </>
      )}
    </div>
  );
};

export default SelfieOnboarding;
